/** Acquisition parameters scrubbed only when they form a recognized KOR email arrival. */
export const EMAIL_ATTRIBUTION_UTM_PARAMETERS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
] as const;

/** Signed-link parameters that remain sensitive even outside a complete attribution query. */
export const EMAIL_ATTRIBUTION_SENSITIVE_QUERY_PARAMETERS = [
  'delivery_id',
  'email_link_token',
] as const;

/** Complete query contract removed from a recognized KOR email landing URL. */
export const EMAIL_ATTRIBUTION_QUERY_PARAMETERS = [
  ...EMAIL_ATTRIBUTION_UTM_PARAMETERS,
  ...EMAIL_ATTRIBUTION_SENSITIVE_QUERY_PARAMETERS,
] as const;

/** Campaign keys currently classified for recipient-level notification attribution. */
export const EMAIL_NOTIFICATION_CAMPAIGN_KEYS = [
  'creator_message_to_members',
  'member_message_to_creator',
  'creator_post_published',
  'community_activity',
  'discussion_reply',
  'space_access_granted',
  'mention',
  'challenge_completed',
  'exclusive_content_published',
  'weekly_digest',
] as const;

/** A stable, repository-classified notification email campaign. */
export type EmailNotificationCampaignKey = (typeof EMAIL_NOTIFICATION_CAMPAIGN_KEYS)[number];

/** Client surfaces accepted by the authenticated landing verifier. */
export type EmailNotificationSurface = 'fan_web' | 'creator_web' | 'ios' | 'android';

/** Minimal untrusted payload retained through authentication; it never grants content access. */
export interface PendingEmailNotificationAttribution {
  deliveryId: string;
  emailLinkToken: string;
  campaignKey: EmailNotificationCampaignKey;
  linkKey: string;
  destinationPath: string;
  /** Untrusted URL hint used only to coordinate the verifier with a loading destination. */
  destinationReference?: string;
  storedAt: number;
}

/** Request accepted by the authenticated notification email landing verifier. */
export interface EmailNotificationLandingRequest {
  delivery_id: string;
  email_link_token: string;
  surface: EmailNotificationSurface;
  destination_path: string;
}

/** Server-derived attribution context returned after ownership and destination verification. */
export interface VerifiedEmailNotificationAttribution {
  attributionContext: string;
  expiresAt: string;
  campaignKey: EmailNotificationCampaignKey;
  linkKey: string;
  destinationType: string;
  deliveryId: string;
  ownerUserId: string;
  destinationPath: string;
  sourceType: string;
  sourceId: string;
  creatorId?: string;
  verifiedAt: number;
}

/** Wire response from the authenticated notification email landing verifier. */
export interface EmailNotificationLandingResponse {
  attribution_context: string;
  expires_at: string;
  campaign_key: EmailNotificationCampaignKey;
  link_key: string;
  destination_type: string;
  source_type: string;
  source_id: string;
  creator_id?: string | null;
}

/** Safe client-reported properties for a product event; authoritative attribution remains server-owned. */
export interface ClientReportedEmailProductAttribution {
  notification_source: 'email';
  email_campaign_key: EmailNotificationCampaignKey;
  email_delivery_id: string;
  email_attribution_authority: 'client_reported';
}

/** Opaque action request field that lets the backend validate and enrich a product conversion. */
export interface EmailAttributedActionRequest {
  email_attribution_context: string;
}

/** Paired backend request and explicitly client-reported analytics seams for one action. */
export interface EmailProductAttributionSeams {
  actionRequest: EmailAttributedActionRequest;
  clientEventProperties: ClientReportedEmailProductAttribution;
}

/** Server-owned non-conversation conversion names accepted by the product-action endpoint. */
export type EmailNotificationProductActionEventName =
  | 'post_viewed'
  | 'community_opened'
  | 'content_opened'
  | 'challenge_opened';

/** Authenticated request that lets the backend re-verify one non-conversation conversion. */
export interface EmailNotificationProductActionRequest {
  event_name: EmailNotificationProductActionEventName;
  email_attribution_context: string;
  resource_id: string;
}

const DELIVERY_UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
// Rails MessageVerifier uses standard/Base64url payloads followed by a `--` signature.
const SIGNED_TOKEN_PATTERN = /^[A-Za-z0-9+/_=.-]{16,2048}$/;
const BOUNDED_KEY_PATTERN = /^[a-z0-9][a-z0-9_-]{0,63}$/i;
// Legacy KOR/Firebase principal IDs may begin with `-` (for example, creator IDs).
const BOUNDED_PRINCIPAL_ID_PATTERN = /^[a-z0-9_-]{1,64}$/i;
const BOUNDED_DESTINATION_PATH_PATTERN = /^\/(?!\/)[^?#]{0,511}$/;
const PENDING_ATTRIBUTION_TTL_MS = 10 * 60 * 1000;
const MAX_STORED_ATTRIBUTIONS = 10;
const completedWebVerificationKeys = new Set<string>();

const webVerificationKey = (
  attribution: Pick<PendingEmailNotificationAttribution, 'deliveryId' | 'linkKey'>,
) => `${attribution.deliveryId}:${attribution.linkKey}`;

/** Browser session key for typed pending notification email attribution payloads. */
export const PENDING_EMAIL_ATTRIBUTION_STORAGE_KEY = 'notification_email_pending_attribution_v1';

/** Browser session key for independently addressable verified notification email contexts. */
export const VERIFIED_EMAIL_ATTRIBUTION_STORAGE_KEY = 'notification_email_verified_contexts_v1';

/** Browser marker that keeps a signed arrival through logout after a recipient mismatch. */
export const EMAIL_ATTRIBUTION_ACCOUNT_SWITCH_REQUIRED_STORAGE_KEY =
  'notification_email_account_switch_required';

/** Browser key shared with the legacy weekly compatibility hook. */
export const LEGACY_WEEKLY_PENDING_ATTRIBUTION_STORAGE_KEY =
  'weekly_digest_legacy_pending_attribution_v1';

/** Browser event emitted when a verifier stores a new server-issued attribution context. */
export const EMAIL_ATTRIBUTION_CONTEXT_CHANGED_EVENT =
  'kor:notification-email-attribution-context-changed';

const isCampaignKey = (value: string): value is EmailNotificationCampaignKey =>
  (EMAIL_NOTIFICATION_CAMPAIGN_KEYS as readonly string[]).includes(value);

/** Matches frontend routes to the exact path spelling used by signed backend links. */
export function canonicalizeEmailAttributionDestinationPath(destinationPath: string): string {
  return destinationPath === '/' ? destinationPath : destinationPath.replace(/\/$/, '');
}

/** Parses the bounded unsigned weekly compatibility join key without accepting other campaigns. */
export function parseLegacyWeeklyDigestAttribution(
  query: string | URLSearchParams,
): { deliveryId: string; card: string } | null {
  const parameters =
    typeof query === 'string'
      ? new URLSearchParams(query.startsWith('?') ? query.slice(1) : query)
      : query;
  const deliveryId = parameters.get('delivery_id') || '';
  const card = parameters.get('utm_content') || '';
  if (
    parameters.has('email_link_token') ||
    parameters.get('utm_source') !== 'sendgrid' ||
    parameters.get('utm_medium') !== 'email' ||
    parameters.get('utm_campaign') !== 'weekly_digest' ||
    !DELIVERY_UUID_PATTERN.test(deliveryId) ||
    !BOUNDED_KEY_PATTERN.test(card)
  ) {
    return null;
  }
  return { deliveryId, card };
}

/** Reads the allowlisted source identifier used to match one landing to one product action. */
export function readEmailAttributionDestinationReference(
  query: string | URLSearchParams,
  destinationPath?: string,
): string | undefined {
  const parameters =
    typeof query === 'string'
      ? new URLSearchParams(query.startsWith('?') ? query.slice(1) : query)
      : query;
  const queryReference =
    parameters.get('messageId') ||
    parameters.get('conversationId') ||
    parameters.get('conversation_id') ||
    parameters.get('postId') ||
    parameters.get('topicId') ||
    parameters.get('filter_by_message_id') ||
    parameters.get('scrollToPostId') ||
    parameters.get('scroll_to_post_id') ||
    parameters.get('notebook_id') ||
    parameters.get('slug') ||
    parameters.get('main_post_ref');
  if (queryReference) return queryReference;

  const pathReference = destinationPath?.match(
    /\/(?:posts?|contents?|content)\/([a-z0-9_-]+)\/?$/i,
  )?.[1];
  return pathReference || undefined;
}

/** Detects a notification URL that telemetry must not observe, without suppressing generic UTMs. */
export function shouldSuppressTelemetryForKorEmailAttribution(
  query: string | URLSearchParams,
  destinationPath = '/',
): boolean {
  const parameters =
    typeof query === 'string'
      ? new URLSearchParams(query.startsWith('?') ? query.slice(1) : query)
      : query;
  return (
    isRecognizedKorEmailAttributionQuery(parameters, destinationPath) ||
    EMAIL_ATTRIBUTION_SENSITIVE_QUERY_PARAMETERS.some((parameter) => parameters.has(parameter))
  );
}

/** Stores typed attribution and scrubs browser history synchronously before telemetry initializes. */
export function prepareWebEmailAttributionBeforeTelemetry(): boolean {
  if (typeof window === 'undefined') return false;
  const parameters = new URLSearchParams(window.location.search);
  const destinationReference = readEmailAttributionDestinationReference(
    parameters,
    window.location.pathname,
  );
  const signedAttribution = parseEmailNotificationAttribution(
    parameters,
    Date.now(),
    window.location.pathname,
    destinationReference,
  );
  const legacyWeeklyAttribution = signedAttribution
    ? null
    : parseLegacyWeeklyDigestAttribution(parameters);
  const shouldSuppress = shouldSuppressTelemetryForKorEmailAttribution(
    parameters,
    window.location.pathname,
  );

  if (signedAttribution) {
    storePendingEmailAttribution(sessionStorage, signedAttribution);
  } else if (legacyWeeklyAttribution) {
    sessionStorage.setItem(
      LEGACY_WEEKLY_PENDING_ATTRIBUTION_STORAGE_KEY,
      JSON.stringify(legacyWeeklyAttribution),
    );
  }
  if (shouldSuppress) {
    sessionStorage.setItem('notification_email_disable_session_replay', 'true');
  }

  const sanitizedParameters =
    signedAttribution || legacyWeeklyAttribution
      ? stripEmailAttributionQuery(parameters)
      : stripSensitiveEmailAttributionQuery(parameters);
  if (sanitizedParameters.toString() !== parameters.toString()) {
    const remainingQuery = sanitizedParameters.toString();
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}${remainingQuery ? `?${remainingQuery}` : ''}${
        window.location.hash
      }`,
    );
  }
  return shouldSuppress;
}

/** Parses only a complete, classified, signed notification email attribution query. */
export function parseEmailNotificationAttribution(
  query: string | URLSearchParams,
  now = Date.now(),
  destinationPath = '/',
  destinationReference?: string,
): PendingEmailNotificationAttribution | null {
  const parameters =
    typeof query === 'string'
      ? new URLSearchParams(query.startsWith('?') ? query.slice(1) : query)
      : query;
  const deliveryId = parameters.get('delivery_id') || '';
  const emailLinkToken = parameters.get('email_link_token') || '';
  const campaignKey = parameters.get('utm_campaign') || '';
  const linkKey = parameters.get('utm_content') || '';
  const canonicalDestinationPath = canonicalizeEmailAttributionDestinationPath(destinationPath);

  if (
    parameters.get('utm_source') !== 'sendgrid' ||
    parameters.get('utm_medium') !== 'email' ||
    !DELIVERY_UUID_PATTERN.test(deliveryId) ||
    !SIGNED_TOKEN_PATTERN.test(emailLinkToken) ||
    !isCampaignKey(campaignKey) ||
    !BOUNDED_KEY_PATTERN.test(linkKey) ||
    !BOUNDED_DESTINATION_PATH_PATTERN.test(canonicalDestinationPath) ||
    (destinationReference !== undefined && !BOUNDED_KEY_PATTERN.test(destinationReference))
  ) {
    return null;
  }

  return {
    deliveryId,
    emailLinkToken,
    campaignKey,
    linkKey,
    destinationPath: canonicalDestinationPath,
    destinationReference,
    storedAt: now,
  };
}

/** Reports whether a query is a signed landing or the bounded legacy weekly compatibility shape. */
export function isRecognizedKorEmailAttributionQuery(
  query: string | URLSearchParams,
  destinationPath = '/',
): boolean {
  const parameters =
    typeof query === 'string'
      ? new URLSearchParams(query.startsWith('?') ? query.slice(1) : query)
      : query;
  return !!(
    parseEmailNotificationAttribution(
      parameters,
      Date.now(),
      destinationPath,
      readEmailAttributionDestinationReference(parameters, destinationPath),
    ) || parseLegacyWeeklyDigestAttribution(parameters)
  );
}

/** Removes every notification attribution field while preserving unrelated query parameters. */
export function stripEmailAttributionQuery(parameters: URLSearchParams): URLSearchParams {
  const sanitized = new URLSearchParams(parameters.toString());
  EMAIL_ATTRIBUTION_QUERY_PARAMETERS.forEach((parameter) => sanitized.delete(parameter));
  return sanitized;
}

/** Removes only signed-link secrets while preserving ordinary or unrecognized acquisition UTMs. */
export function stripSensitiveEmailAttributionQuery(parameters: URLSearchParams): URLSearchParams {
  const sanitized = new URLSearchParams(parameters.toString());
  EMAIL_ATTRIBUTION_SENSITIVE_QUERY_PARAMETERS.forEach((parameter) => sanitized.delete(parameter));
  return sanitized;
}

/** Builds the verifier request without forwarding untrusted UTM labels. */
export function buildEmailNotificationLandingRequest(
  attribution: PendingEmailNotificationAttribution,
  surface: EmailNotificationSurface,
): EmailNotificationLandingRequest {
  return {
    delivery_id: attribution.deliveryId,
    email_link_token: attribution.emailLinkToken,
    surface,
    destination_path: attribution.destinationPath,
  };
}

/** Converts a successful verifier response into a locally selectable typed context. */
export function buildVerifiedEmailAttribution(
  pending: PendingEmailNotificationAttribution,
  response: EmailNotificationLandingResponse,
  ownerUserId: string,
  now = Date.now(),
): VerifiedEmailNotificationAttribution | null {
  if (
    !ownerUserId ||
    !response.attribution_context ||
    !SIGNED_TOKEN_PATTERN.test(response.attribution_context) ||
    !isCampaignKey(response.campaign_key) ||
    !BOUNDED_KEY_PATTERN.test(response.link_key) ||
    !BOUNDED_KEY_PATTERN.test(response.destination_type) ||
    !BOUNDED_KEY_PATTERN.test(response.source_type) ||
    !BOUNDED_KEY_PATTERN.test(response.source_id) ||
    (response.creator_id != null && !BOUNDED_PRINCIPAL_ID_PATTERN.test(response.creator_id)) ||
    !Number.isFinite(Date.parse(response.expires_at))
  ) {
    return null;
  }

  return {
    attributionContext: response.attribution_context,
    expiresAt: response.expires_at,
    campaignKey: response.campaign_key,
    linkKey: response.link_key,
    destinationType: response.destination_type,
    deliveryId: pending.deliveryId,
    ownerUserId,
    destinationPath: pending.destinationPath,
    sourceType: response.source_type,
    sourceId: response.source_id,
    creatorId: response.creator_id || undefined,
    verifiedAt: now,
  };
}

function parseStoredArray<T>(storage: Pick<Storage, 'getItem'>, key: string): T[] {
  try {
    const parsed = JSON.parse(storage.getItem(key) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** Retains multiple typed signed arrivals through login without keeping a raw URL. */
export function storePendingEmailAttribution(
  storage: Pick<Storage, 'getItem' | 'setItem'>,
  attribution: PendingEmailNotificationAttribution,
): void {
  const existing = parseStoredArray<PendingEmailNotificationAttribution>(
    storage,
    PENDING_EMAIL_ATTRIBUTION_STORAGE_KEY,
  ).filter(
    (candidate) =>
      candidate.deliveryId !== attribution.deliveryId || candidate.linkKey !== attribution.linkKey,
  );
  storage.setItem(
    PENDING_EMAIL_ATTRIBUTION_STORAGE_KEY,
    JSON.stringify([...existing, attribution].slice(-MAX_STORED_ATTRIBUTIONS)),
  );
}

/** Returns unexpired pending arrivals in insertion order and deletes malformed or expired entries. */
export function readPendingEmailAttributions(
  storage: Pick<Storage, 'getItem' | 'setItem'>,
  now = Date.now(),
): PendingEmailNotificationAttribution[] {
  const active = parseStoredArray<PendingEmailNotificationAttribution>(
    storage,
    PENDING_EMAIL_ATTRIBUTION_STORAGE_KEY,
  ).filter(
    (candidate) =>
      DELIVERY_UUID_PATTERN.test(candidate?.deliveryId || '') &&
      SIGNED_TOKEN_PATTERN.test(candidate?.emailLinkToken || '') &&
      isCampaignKey(candidate?.campaignKey || '') &&
      BOUNDED_KEY_PATTERN.test(candidate?.linkKey || '') &&
      BOUNDED_DESTINATION_PATH_PATTERN.test(candidate?.destinationPath || '') &&
      (candidate?.destinationReference === undefined ||
        BOUNDED_KEY_PATTERN.test(candidate.destinationReference)) &&
      Number.isFinite(candidate?.storedAt) &&
      now - candidate.storedAt <= PENDING_ATTRIBUTION_TTL_MS,
  );
  storage.setItem(PENDING_EMAIL_ATTRIBUTION_STORAGE_KEY, JSON.stringify(active));
  return active;
}

/** Matches exact stable references across route aliases, falling back to canonical paths. */
function matchesEmailAttributionDestination(
  candidate: { destinationPath?: string; destinationReference?: string },
  canonicalDestinationPath: string,
  destinationReference?: string,
): boolean {
  if (destinationReference) {
    return candidate.destinationReference === destinationReference;
  }
  return (
    canonicalizeEmailAttributionDestinationPath(candidate.destinationPath || '') ===
    canonicalDestinationPath
  );
}

/** Selects the exact unfinished signed arrival matching a web destination. */
export function selectPendingEmailAttributionForDestination(
  storage: Pick<Storage, 'getItem' | 'setItem'>,
  destinationPath: string,
  destinationReference?: string,
  currentQuery?: string | URLSearchParams,
): PendingEmailNotificationAttribution | null {
  const canonicalDestinationPath = canonicalizeEmailAttributionDestinationPath(destinationPath);
  const storedMatch = [...readPendingEmailAttributions(storage)]
    .reverse()
    .find(
      (candidate) =>
        matchesEmailAttributionDestination(
          candidate,
          canonicalDestinationPath,
          destinationReference,
        ) && !completedWebVerificationKeys.has(webVerificationKey(candidate)),
    );
  if (storedMatch || !currentQuery) return storedMatch || null;

  const queryReference = readEmailAttributionDestinationReference(currentQuery, destinationPath);
  const queryAttribution = parseEmailNotificationAttribution(
    currentQuery,
    Date.now(),
    canonicalDestinationPath,
    queryReference,
  );
  return queryAttribution &&
    matchesEmailAttributionDestination(
      queryAttribution,
      canonicalDestinationPath,
      destinationReference,
    ) &&
    !completedWebVerificationKeys.has(webVerificationKey(queryAttribution))
    ? queryAttribution
    : null;
}

/** Checks whether a source-matched web action should wait for landing verification. */
export function hasPendingEmailAttributionForDestination(
  storage: Pick<Storage, 'getItem' | 'setItem'>,
  destinationPath: string,
  destinationReference?: string,
  currentQuery?: string | URLSearchParams,
): boolean {
  return !!selectPendingEmailAttributionForDestination(
    storage,
    destinationPath,
    destinationReference,
    currentQuery,
  );
}

/** Marks a retained web arrival as actively verifying so destination actions briefly wait. */
export function markWebEmailAttributionVerificationStarted(
  attribution: PendingEmailNotificationAttribution,
): void {
  completedWebVerificationKeys.delete(webVerificationKey(attribution));
}

/** Marks one verifier attempt complete; transient failures may remain retained for retry. */
export function markWebEmailAttributionVerificationCompleted(
  attribution: PendingEmailNotificationAttribution,
): void {
  completedWebVerificationKeys.add(webVerificationKey(attribution));
}

/** Waits briefly for a matching web landing verifier, then fails open for the product action. */
export function waitForWebEmailAttributionVerification(
  storage: Pick<Storage, 'getItem' | 'setItem'>,
  destinationPath: string,
  destinationReference?: string,
  currentQuery?: string | URLSearchParams,
  timeoutMs = 3_000,
): Promise<void> {
  const canonicalDestinationPath = canonicalizeEmailAttributionDestinationPath(destinationPath);
  if (
    typeof window === 'undefined' ||
    !hasPendingEmailAttributionForDestination(
      storage,
      canonicalDestinationPath,
      destinationReference,
      currentQuery,
    )
  ) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    let timeoutId: ReturnType<typeof setTimeout>;
    const finish = () => {
      window.removeEventListener(EMAIL_ATTRIBUTION_CONTEXT_CHANGED_EVENT, handleChange);
      clearTimeout(timeoutId);
      resolve();
    };
    const handleChange = (event: Event) => {
      const detail = (event as CustomEvent).detail as
        | { destinationPath?: string; destinationReference?: string }
        | undefined;
      if (
        !detail ||
        matchesEmailAttributionDestination(detail, canonicalDestinationPath, destinationReference)
      ) {
        finish();
      }
    };
    window.addEventListener(EMAIL_ATTRIBUTION_CONTEXT_CHANGED_EVENT, handleChange);
    timeoutId = setTimeout(finish, timeoutMs);
  });
}

/** Removes one pending arrival after a successful or definitive verifier result. */
export function removePendingEmailAttribution(
  storage: Pick<Storage, 'getItem' | 'setItem'>,
  target: Pick<PendingEmailNotificationAttribution, 'deliveryId' | 'linkKey'>,
): void {
  const remaining = parseStoredArray<PendingEmailNotificationAttribution>(
    storage,
    PENDING_EMAIL_ATTRIBUTION_STORAGE_KEY,
  ).filter(
    (candidate) =>
      candidate.deliveryId !== target.deliveryId || candidate.linkKey !== target.linkKey,
  );
  storage.setItem(PENDING_EMAIL_ATTRIBUTION_STORAGE_KEY, JSON.stringify(remaining));
}

/** Stores verified deliveries independently so one email cannot overwrite another. */
export function storeVerifiedEmailAttribution(
  storage: Pick<Storage, 'getItem' | 'setItem'>,
  attribution: VerifiedEmailNotificationAttribution,
): void {
  const existing = readVerifiedEmailAttributions(storage).filter(
    (candidate) =>
      candidate.deliveryId !== attribution.deliveryId || candidate.linkKey !== attribution.linkKey,
  );
  storage.setItem(
    VERIFIED_EMAIL_ATTRIBUTION_STORAGE_KEY,
    JSON.stringify([...existing, attribution].slice(-MAX_STORED_ATTRIBUTIONS)),
  );
}

/** Reads only server-verified contexts whose server expiry is still in the future. */
export function readVerifiedEmailAttributions(
  storage: Pick<Storage, 'getItem' | 'setItem'>,
  now = Date.now(),
): VerifiedEmailNotificationAttribution[] {
  const active = parseStoredArray<VerifiedEmailNotificationAttribution>(
    storage,
    VERIFIED_EMAIL_ATTRIBUTION_STORAGE_KEY,
  ).filter(
    (candidate) =>
      DELIVERY_UUID_PATTERN.test(candidate?.deliveryId || '') &&
      SIGNED_TOKEN_PATTERN.test(candidate?.attributionContext || '') &&
      isCampaignKey(candidate?.campaignKey || '') &&
      BOUNDED_KEY_PATTERN.test(candidate?.linkKey || '') &&
      BOUNDED_KEY_PATTERN.test(candidate?.destinationType || '') &&
      BOUNDED_KEY_PATTERN.test(candidate?.sourceType || '') &&
      BOUNDED_KEY_PATTERN.test(candidate?.sourceId || '') &&
      (candidate?.creatorId === undefined ||
        BOUNDED_PRINCIPAL_ID_PATTERN.test(candidate.creatorId)) &&
      typeof candidate?.ownerUserId === 'string' &&
      candidate.ownerUserId.length > 0 &&
      Date.parse(candidate?.expiresAt || '') > now,
  );
  storage.setItem(VERIFIED_EMAIL_ATTRIBUTION_STORAGE_KEY, JSON.stringify(active));
  return active;
}

/** Selects the most-recent context matching an authenticated owner and exact server source. */
export function selectEmailAttributionForProductAction(
  storage: Pick<Storage, 'getItem' | 'setItem'>,
  destinationType: string,
  ownerUserId: string,
  destination: { resourceId: string; deliveryId?: string; linkKey?: string },
  now = Date.now(),
): VerifiedEmailNotificationAttribution | null {
  if (
    !BOUNDED_KEY_PATTERN.test(destinationType) ||
    !ownerUserId ||
    !BOUNDED_KEY_PATTERN.test(destination.resourceId)
  ) {
    return null;
  }
  const candidates = readVerifiedEmailAttributions(storage, now)
    .filter(
      (candidate) =>
        candidate.destinationType === destinationType &&
        candidate.ownerUserId === ownerUserId &&
        (!destination.deliveryId || candidate.deliveryId === destination.deliveryId) &&
        (!destination.linkKey || candidate.linkKey === destination.linkKey) &&
        candidate.sourceId === destination.resourceId,
    )
    .sort((left, right) => right.verifiedAt - left.verifiedAt);
  return candidates[0] || null;
}

/** Returns the opaque verifier context field for a backend-owned product action API. */
export function buildEmailAttributedActionRequest(
  attribution: VerifiedEmailNotificationAttribution,
): EmailAttributedActionRequest {
  return { email_attribution_context: attribution.attributionContext };
}

const EMAIL_PRODUCT_ACTION_EVENT_BY_DESTINATION_TYPE: Record<
  string,
  EmailNotificationProductActionEventName
> = {
  post: 'post_viewed',
  reply_thread: 'post_viewed',
  mention: 'post_viewed',
  digest: 'post_viewed',
  community: 'community_opened',
  space: 'community_opened',
  content: 'content_opened',
  challenge: 'challenge_opened',
};

/** Builds a backend-verified product action only for accepted non-conversation destinations. */
export function buildEmailNotificationProductActionRequest(
  attribution: VerifiedEmailNotificationAttribution,
  resourceId: string,
): EmailNotificationProductActionRequest | null {
  const eventName = EMAIL_PRODUCT_ACTION_EVENT_BY_DESTINATION_TYPE[attribution.destinationType];
  if (!eventName || !BOUNDED_KEY_PATTERN.test(resourceId) || resourceId !== attribution.sourceId) {
    return null;
  }
  return {
    event_name: eventName,
    email_attribution_context: attribution.attributionContext,
    resource_id: resourceId,
  };
}

/** Labels browser product analytics as client-reported while carrying only safe join keys. */
export function buildClientReportedEmailAttribution(
  attribution: VerifiedEmailNotificationAttribution,
): ClientReportedEmailProductAttribution {
  return {
    notification_source: 'email',
    email_campaign_key: attribution.campaignKey,
    email_delivery_id: attribution.deliveryId,
    email_attribution_authority: 'client_reported',
  };
}

/** Selects one matching web context and returns separate API and analytics attribution seams. */
export function resolveWebEmailProductAttribution(
  destinationType: string,
  ownerUserId: string,
  resourceId: string,
): EmailProductAttributionSeams | null {
  if (typeof window === 'undefined') return null;
  const attribution = selectEmailAttributionForProductAction(
    sessionStorage,
    destinationType,
    ownerUserId,
    { resourceId },
  );
  if (!attribution) return null;
  return {
    actionRequest: buildEmailAttributedActionRequest(attribution),
    clientEventProperties: buildClientReportedEmailAttribution(attribution),
  };
}

/** Clears pending and verified notification attribution during logout or account switching. */
export function clearEmailNotificationAttribution(storage: Pick<Storage, 'removeItem'>): void {
  storage.removeItem(PENDING_EMAIL_ATTRIBUTION_STORAGE_KEY);
  storage.removeItem(VERIFIED_EMAIL_ATTRIBUTION_STORAGE_KEY);
  storage.removeItem(EMAIL_ATTRIBUTION_ACCOUNT_SWITCH_REQUIRED_STORAGE_KEY);
  completedWebVerificationKeys.clear();
}

/** Clears account-owned verified contexts while preserving a just-arrived link through login. */
export function clearVerifiedEmailNotificationAttribution(
  storage: Pick<Storage, 'removeItem'>,
): void {
  storage.removeItem(VERIFIED_EMAIL_ATTRIBUTION_STORAGE_KEY);
  completedWebVerificationKeys.clear();
}

/** Clears an auth session while retaining only the typed pending link and notification handoff markers. */
export function clearSessionStoragePreservingPendingEmailAttribution(
  storage: Pick<Storage, 'getItem' | 'setItem' | 'clear'>,
): void {
  const pendingAttribution = storage.getItem(PENDING_EMAIL_ATTRIBUTION_STORAGE_KEY);
  const replayDisabled = storage.getItem('notification_email_disable_session_replay');
  const accountSwitchRequired = storage.getItem(
    EMAIL_ATTRIBUTION_ACCOUNT_SWITCH_REQUIRED_STORAGE_KEY,
  );
  storage.clear();
  completedWebVerificationKeys.clear();
  if (pendingAttribution) {
    storage.setItem(PENDING_EMAIL_ATTRIBUTION_STORAGE_KEY, pendingAttribution);
  }
  if (replayDisabled === 'true') {
    storage.setItem('notification_email_disable_session_replay', 'true');
  }
  if (accountSwitchRequired === 'true') {
    storage.setItem(EMAIL_ATTRIBUTION_ACCOUNT_SWITCH_REQUIRED_STORAGE_KEY, 'true');
  }
}
